import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FlightBookingIssueControllerService } from '@booking/angular-booking-admin-service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BookingIssueService } from 'src/app/services/booking-issue/booking-issue.service';
import { TooltipPosition, TooltipTheme } from '@shared/enum/tooltip.enums';
import { getBookingStatusKey, getBookingStatusStyle } from '@shared/utils/helper';
import { CountryCodeService } from 'src/app/services/country-code.service';

export enum TICKET_STATUS {
    HOLD = 'Hold',
    EXPIRED = 'Expired',
    TICKETING = 'Ticketing',
    TICKETED = 'Ticketed'
}

@Component({
    selector: 'app-view-details-flight',
    templateUrl: './view-details-flight.component.html',
    styleUrls: ['./view-details-flight.component.scss']
})
export class ViewDetailsFlightComponent {
    @Input() type: string;
    @Input() flightDetail: any;
    @Input() id: any = '';
    @Output() closePopupEmit = new EventEmitter<boolean>();

    detailsData: any;
    querySubscription: Subscription;
    pinCode: string;
    isLoading: boolean = true;
    onDestroy$: Subject<void> = new Subject();
    isClickBack: boolean = false;
    isShowChangeRequestHistoryPopup: boolean = false;
    changeRequestHistory: any;
    currentIndexTag: number = 0;
    currentSegments: any;
    currentSegmentType: string;
    currentFareRule: any;
    fareRuleVi: string;
    resource: string;
    bookingStatusStyle: string = '';
    bookingStatus: string = '';
    TooltipPosition: typeof TooltipPosition = TooltipPosition;
    TooltipTheme: typeof TooltipTheme = TooltipTheme;
    bookingInfo: any;
    flightInfo: any;
    passengers: any[] = [];
    itineraries: any;
    availableItineraries: any[] = [];
    changeNameData: any[] = [];
    addOnsData: any[] = [];

    constructor(
        private bookingIssueService: BookingIssueService,
        private translateService: TranslateService,
        private flightBookingIssueControllerService: FlightBookingIssueControllerService,
        private countryCodeService: CountryCodeService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['id'] && changes['id'].currentValue) {
            this.isLoading = true;
            this.getViewDetailsData();
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        if (!this.isClickBack) {
            this.bookingIssueService.setCalendarValue(null);
        }
    }

    getViewDetailsData() {
        this.flightBookingIssueControllerService
            .viewChildReservation(this.id)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (res: any) => {
                    if (res.success) {
                        // CANCEL
                        if (this.type === 'CANCEL') {
                            this.detailsData = res.data.cancel_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.itineraries = this.flightDetail.flight_info.itineraries;
                            this.detailsData.canceled_list.forEach((item: any) => {
                                this.availableItineraries.push(item[0].itinerary_index);
                            });
                            this.currentIndexTag = this.detailsData.canceled_list[0][0].itinerary_index;
                            this.passengers = this.detailsData.canceled_list[0];
                            this.bookingInfo = this.flightDetail.booking_info;
                            this.flightInfo = this.flightDetail.flight_info;
                        }
                        // FINDING FLIGHT
                        if (this.type === 'FINDING_FLIGHT') {
                            this.detailsData = res.data.finding_flight_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            // Set itineraries data
                            const source = this.detailsData.action !== 'REQUEST_REFUND'
                                ? this.detailsData.flight_info
                                : this.detailsData.itineraries_for_refund;
                            this.itineraries = (source || []).filter((item: any) => item);
                            // Set Flight info
                            this.flightInfo = {
                                source: null,
                                inbound_source: null
                            };
                            this.itineraries.forEach((item: any) => {
                                const mappedResource = this.mapResource(item?.resource);
                                if (item.segment_type == 'departure') {
                                    this.flightInfo.source = mappedResource
                                } else if (item.segment_type == 'arrival') {
                                    this.flightInfo.inbound_source = mappedResource
                                }
                                // Set round tag
                                this.availableItineraries.push(item.segment_type == 'departure' ? 0 : 1);
                            });
                            this.currentIndexTag = this.availableItineraries[0];
                            this.passengers = this.itineraries[0].passengers;

                            const [first, second] = this.itineraries;
                            this.bookingInfo = {
                                reference_id: first && first.segment_type === 'departure' ? first.pnr_code : null,
                                inbound_reference_id: first && first.segment_type !== 'departure' ? first.pnr_code : second?.pnr_code ?? null
                            };
                        }
                        // RESCHEDULE
                        if (this.type === 'RESCHEDULE') {
                            this.detailsData = res.data.reschedule_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            // Set itineraries data
                            const source = this.detailsData.itineraries;
                            this.itineraries = (source || []).filter((item: any) => item);
                            // Set Flight info
                            this.flightInfo = {
                                source: null,
                                inbound_source: null
                            };
                            this.itineraries.forEach((item: any) => {
                                const mappedResource = this.mapResource(item?.resource);
                                if (item.segment_type == 'departure') {
                                    this.flightInfo.source = mappedResource
                                } else if (item.segment_type == 'arrival') {
                                    this.flightInfo.inbound_source = mappedResource
                                }
                                // Set round tag
                                this.availableItineraries.push(item.segment_type == 'departure' ? 0 : 1);
                            });
                            this.currentIndexTag = this.availableItineraries[0];

                            this.passengers = this.itineraries[0].passengers;
                            const [first, second] = this.itineraries;

                            this.bookingInfo = {
                                reference_id: first && first.segment_type === 'departure' ? first.pnr_code : null,
                                inbound_reference_id: first && first.segment_type !== 'departure' ? first.pnr_code : second?.pnr_code ?? null
                            };
                        }
                        // CHANGE NAME
                        if (this.type === 'CHANGE_NAME') {
                            this.changeNameData = [];
                            this.detailsData = res.data.change_name_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.detailsData.change_passenger_name_history.forEach((passenger: any) => {
                                const oldName = `${passenger.old_name.title}. ${passenger.old_name.last_name} ${passenger.old_name.middle_and_first_name}`;
                                const newName = `${passenger.new_name.title}. ${passenger.new_name.last_name} ${passenger.new_name.middle_and_first_name}`;
                                if (oldName != newName) {
                                    this.changeNameData.push(passenger);
                                }
                            });
                        }
                        // ADDITIONAL PURCHASING
                        if (this.type === 'ADD_ONS') {
                            this.addOnsData = [];
                            this.detailsData = res.data.add_on_history.find(
                                (item: any) => item.view_detail === this.id
                            );
                            this.detailsData.add_on_by_itinerary.forEach((itinerary: any) => {
                                this.addOnsData[itinerary.itinerary_index] = {
                                    itinerary_index: itinerary.itinerary_index,
                                    origin: itinerary.segment_iata_code_list[0][0],
                                    destination: itinerary.segment_iata_code_list[0][1],
                                    addonByPassengers: []
                                };
                                itinerary.add_ons.forEach((passenger: any) => {
                                    const passengerInfo = itinerary.passengers_info.find((p: any) => p.passenger_id === passenger.passenger_id);
                                    const formatData = this.groupAddOnsBySegment(passenger);
                                    this.addOnsData[itinerary.itinerary_index].addonByPassengers.push({
                                        passengerInfo,
                                        data: formatData
                                    });
                                });
                            });
                        }
                        // Load itinerary
                        if (this.type == 'CANCEL' || this.type == 'RESCHEDULE' || this.type == 'FINDING_FLIGHT') {
                            const itinerary = this.itineraries[0];
                            if (itinerary) {
                                this.currentSegments = itinerary.segments;
                                this.currentSegmentType = itinerary.segment_type;
                            }
                        }
                        // Load status and color booking status.
                        this.bookingStatus = getBookingStatusKey(res.data.reservation_status);
                        this.bookingStatusStyle = getBookingStatusStyle(res.data.reservation_status);
                    }
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                }
            });
    }

    mapResource(value: string): string {
        const map: { [key: string]: string } = {
            ag_hpl: 'HPL',
            ag_tbo: 'TBO',
            d_vietjet: 'VIETJET'
        };
        return map[value] || value;
    }

    changeCurrentTag(currentIndex: number, index: number) {
        this.currentIndexTag = currentIndex;
        // Re init data when mode changes
        const itinerary = this.itineraries[index];
        if (itinerary) {
            this.currentSegments = itinerary.segments;
            this.currentSegmentType = itinerary.segment_type;
        }

        if (this.type === 'RESCHEDULE') {
            this.passengers = this.detailsData.itineraries[this.currentIndexTag].passengers;
        } else if (this.type === 'CANCEL') {
            this.passengers = this.detailsData.canceled_list[index];
        }
    }

    handleHidePopup() {
        this.closePopupEmit.emit();
    }

    formatTime(timeString: string): string {
        const timeParts = timeString.split(':');
        const date = new Date();
        date.setHours(Number(timeParts[0]));
        date.setMinutes(Number(timeParts[1]));

        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    }

    formatDuration(timeString: string, space: boolean): string {
        const timeParts = timeString.split(':');
        const hours = Number(timeParts[0]);
        const minutes = Number(timeParts[1]);

        let formattedDuration = '';

        if (hours > 0) {
            if (space) {
                formattedDuration += `${hours}h `;
            } else {
                formattedDuration += `${hours}h`;
            }
        }

        formattedDuration += `${minutes}m`;

        return formattedDuration;
    }

    formatBaggages(num: any, quality: number, type: any): string {
        if (
            quality === 0 &&
            (type === 'K' || type === 'kg' || type == 'kilograms' || type === 'P')
        ) {
            return '';
        }

        switch (type) {
            case 'N':
                return `${num} ${this.translateService.instant('COMMON.PKG')}`;
            case 'K':
            case 'kg':
            case 'kilograms':
                return `${num}x${quality} kg`;
            case 'P':
                return `${num}x${quality} lb`;
            case 'pc(s)':
                return `${num} ${this.translateService.instant('COMMON.PCS')}`;
            default:
                if (quality && quality > 0) return `${num}x${quality} ${type}`;
                return `${num} ${type}`;
        }
    }

    getUTCUser(): string {
        return this.countryCodeService.getUTCOffset();
    }

    groupAddOnsBySegment(passenger: any) {
        const segmentsMap: Record<string, any> = {};

        const addSegment = (list: any[], type: 'seat' | 'meal' | 'baggage') => {
            if (!Array.isArray(list)) return;

            for (const item of list) {
                const key = `${item.origin_iata_code}-${item.destination_iata_code}`;
                if (!segmentsMap[key]) {
                    segmentsMap[key] = {
                        origin: item.origin_iata_code,
                        destination: item.destination_iata_code,
                        seat: null,
                        meals: null,
                        extra_baggage: null
                    };
                }

                if (type === 'seat') segmentsMap[key].seat = item;
                if (type === 'meal') segmentsMap[key].meals = item;
                if (type === 'baggage') segmentsMap[key].extra_baggage = item;
            }
        };

        addSegment(passenger.passenger_seat_list, 'seat');
        addSegment(passenger.extra_meal_list, 'meal');
        addSegment(passenger.extra_baggage_list, 'baggage');

        return Object.values(segmentsMap);
    }

    handleClosePopup() {
        this.closePopupEmit.emit();
    }
}
